import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';
import CollapsibleSection from '../../components/CollapsibleSection';
import {
  isBucketDown,
  bucketStartSeconds,
  formatEdge,
} from '../../libs/heartbeatHistory';

const StyledDiv = styledComponent('div', {
  marginBottom: theme.spacing.sectionGap,

  // Always visible, not a tooltip: the metric can't be read correctly without
  // it, and hover would hide it from first-time viewers, touch and keyboard.
  '& .fht-caption': {
    ...theme.typography.hint,
    color: theme.colors.textMuted,
    marginBottom: theme.spacing.fieldGap,
    maxWidth: '68ch',
  },
  '& .fht-caption-warn': {
    color: theme.colors.warningText,
  },

  '& .fht-stats': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.gapXl,
    marginBottom: theme.spacing.fieldGap,
  },
  '& .fht-stat-value': {
    ...theme.typography.sectionTitle,
    color: theme.colors.textDark,
    margin: 0,
  },
  '& .fht-stat-label': {
    ...theme.typography.label,
    color: theme.colors.textMuted,
  },
  '& .fht-empty': {
    padding: theme.spacing.emptyStatePadding,
    textAlign: 'center',
    color: theme.colors.textMuted,
  },
  '& .fht-tip': {
    background: theme.colors.bgCard,
    border: theme.borders.card,
    borderRadius: theme.radii.badge,
    padding: theme.spacing.smallButtonPadding,
    ...theme.typography.hint,
    color: theme.colors.textBody,
  },
  '& .fht-tip-pct': {
    color: theme.colors.danger,
    fontWeight: theme.fontWeights.semibold,
  },
});

// Ticks stay short; the tooltip carries the weekday, since a 24h window's two
// ends share the same clock time.
function formatClock(ms) {
  return new Date(ms).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

const EMPTY_AGG = { points: [], total: 0, peak: 0, current: 0 };

// Per-bucket "% missing a heartbeat": a synchronized spike means an outage, not
// scattered faults. Free — the histories already arrived on the rows.
function aggregate(fridges) {
  const histories = fridges
    .map((f) => f.history)
    .filter((h) => h?.buckets?.length);
  const total = histories.length;
  if (total === 0) return EMPTY_AGG;

  // Bucket i is the same instant fleet-wide. A short card shrinks that bucket's
  // denominator rather than counting as "up".
  const axis = histories.reduce((a, b) =>
    b.buckets.length > a.buckets.length ? b : a,
  );
  const points = [];
  let peak = 0;
  for (let i = 0; i < axis.buckets.length; i += 1) {
    let missing = 0;
    let counted = 0;
    for (let h = 0; h < total; h += 1) {
      const ratio = histories[h].buckets[i];
      if (ratio !== undefined) {
        counted += 1;
        if (isBucketDown(ratio)) missing += 1;
      }
    }
    const pct = counted ? (missing / counted) * 100 : 0;
    if (pct > peak) peak = pct;
    const tsSec = bucketStartSeconds(axis, i);
    points.push({
      tsSec,
      label: formatClock(tsSec * 1000),
      missing,
      total: counted,
      pct,
    });
  }
  const current = points.length ? points[points.length - 1].pct : 0;
  return { points, total, peak, current };
}

function TrendTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0].payload;
  return (
    <div className="fht-tip">
      <div>{formatEdge(p.tsSec)}</div>
      <div>
        <span className="fht-tip-pct">{p.pct.toFixed(1)}% missing</span>
        {` (${p.missing} of ${p.total})`}
      </div>
    </div>
  );
}

TrendTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
};

TrendTooltip.defaultProps = {
  active: false,
  payload: null,
};

export default function FleetHeartbeatTrend({ fridges }) {
  const [expanded, setExpanded] = useState(false);

  // Only aggregate while open. `fridges` identity changes on filter edits and
  // Refresh, so the chart tracks both.
  const agg = useMemo(
    () => (expanded ? aggregate(fridges) : EMPTY_AGG),
    [expanded, fridges],
  );

  // A small charted set makes the line coarse — at 3 fridges it can only read
  // 0/33/67/100%. Say so, or a 100% peak reads as a fleet-wide event.
  const coverageWarning =
    agg.total > 0 && agg.total < fridges.length
      ? `Only ${agg.total} of ${fridges.length} fridges in view have heartbeat ` +
        `history, so every percentage here is out of ${agg.total} — one fridge ` +
        `moves the line by ${Math.round(100 / agg.total)} points.`
      : null;

  return (
    <StyledDiv>
      <CollapsibleSection
        title="Heartbeat History (last 24h)"
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
      >
        {agg.total === 0 ? (
          <div className="fht-empty">
            {fridges.length === 0
              ? 'No fridges in the current filter.'
              : 'No heartbeat history available.'}
          </div>
        ) : (
          <>
            <p className="fht-caption">
              Each point is one 5-minute bucket: the share of charted fridges
              that went more than 15 minutes without a heartbeat. A spike that
              hits many fridges at the same moment usually means a network or
              data-pipeline outage, not that each fridge failed on its own.
              {coverageWarning && (
                <span className="fht-caption-warn"> {coverageWarning}</span>
              )}
            </p>
            <div className="fht-stats">
              <div>
                <p className="fht-stat-value">{agg.total}</p>
                {/* Name the denominator rather than imply full coverage. */}
                <span className="fht-stat-label">
                  {agg.total === fridges.length
                    ? 'Fridges in view'
                    : `Fridges charted (of ${fridges.length} in view)`}
                </span>
              </div>
              <div>
                <p className="fht-stat-value">{agg.current.toFixed(1)}%</p>
                <span className="fht-stat-label">Missing now</span>
              </div>
              <div>
                <p className="fht-stat-value">{agg.peak.toFixed(1)}%</p>
                <span className="fht-stat-label">Peak (24h)</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={agg.points}
                margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="label"
                  minTickGap={48}
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  tickFormatter={(v) => `${v}%`}
                  allowDecimals={false}
                  domain={[0, (max) => Math.max(5, Math.ceil(max * 1.1))]}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={<TrendTooltip />} />
                <Line
                  type="monotone"
                  dataKey="pct"
                  stroke={theme.colors.danger}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </CollapsibleSection>
    </StyledDiv>
  );
}

FleetHeartbeatTrend.propTypes = {
  fridges: PropTypes.arrayOf(
    PropTypes.shape({
      locationId: PropTypes.string.isRequired,
      status: PropTypes.string,
      lastHeartbeat: PropTypes.number,
      // From getFleetStatus; null for a fridge with no card or no sparkline yet.
      history: PropTypes.shape({
        startTs: PropTypes.number,
        bucketSeconds: PropTypes.number,
        buckets: PropTypes.arrayOf(PropTypes.number),
      }),
    }),
  ).isRequired,
};
